import { z } from 'zod';
import { ActionSummary } from './action.js';
import { Cidr, IpAddress, IsoTime, ServerId } from './basics.js';
import { GeoInfo } from './country.js';
import { ServerStatus } from './server.js';
/*
 * An address's verdict (P11.1): is it stopped, where, by whom and until when.
 *
 * The agent already follows every CrowdSec decision to count them (section 8.1);
 * it now also reports the ones that are about this server — CrowdSec's own
 * scenarios, Bartizo's bans and a person's `cscli` — as a snapshot. The community
 * blocklists stay counted only: they run to hundreds of thousands of addresses,
 * and an address on one is dropped by the firewall before any web server logs it.
 *
 * The API joins that with Bartizo's actions, the ban safety checks, events and
 * traffic into one answer per address.
 */
/** The most decisions one snapshot carries; past it the snapshot says it is cut short. */
export const MAX_REPORTED_DECISIONS = 5_000;
/**
 * Who made a decision, from CrowdSec's `origin` and Bartizo's mark on its own
 * bans (P5.4 writes the reason as `bartizo: <who>: <why>`): a CrowdSec scenario,
 * a Bartizo ban, a person running `cscli` on the server, or something else.
 */
export const DecisionBy = z.enum(['crowdsec', 'bartizo', 'manual', 'other']);
export const ReportedDecision = z
    .object({
    /** An address, or a range for a range decision. */
    value: z.union([IpAddress, Cidr]),
    scope: z.enum(['ip', 'range']),
    /** `ban`, or a remediation a bouncer supports, such as `captcha`. */
    type: z
        .string()
        .min(1)
        .max(32)
        .regex(/^[a-z_]+$/, 'a decision type'),
    by: DecisionBy,
    /**
     * Which Bartizo rule made it (P11.16): `trigger:<id>` for a CrowdSec trigger,
     * `behaviour:<name>` for a behaviour rule's scenario. Absent for anything else.
     */
    rule: z
        .string()
        .max(80)
        .regex(/^(trigger|behaviour):[a-z0-9][a-z0-9_.-]*$/)
        .optional(),
    /** The scenario, or Bartizo's reason; text from the server, so bounded and printable. */
    scenario: z
        .string()
        .max(256)
        .regex(/^[\x20-\x7E]*$/, 'printable')
        .optional(),
    until: IsoTime,
})
    .strict();
/** `POST /v1/ingest/decisions`: everything in force on this server now, replacing the last. */
export const DecisionSnapshot = z
    .object({
    /** The agent's own, so a snapshot sent twice is stored once. */
    id: z.uuidv7(),
    takenAt: IsoTime,
    decisions: z.array(ReportedDecision).max(MAX_REPORTED_DECISIONS),
    /** More were in force than a snapshot carries; the newest-ending were kept. */
    truncated: z.boolean(),
})
    .strict();
export const DecisionSnapshotReceipt = z.object({
    /** False for a snapshot already stored, or older than the one the API has. */
    stored: z.boolean(),
    decisions: z.number().int().nonnegative(),
});
/**
 * The fleet's answer for an address.
 *
 * - **stopped**: banned on every server that can enforce a ban.
 * - **partly**: banned on some of them.
 * - **pending**: a Bartizo ban is on its way and nothing is in force yet.
 * - **open**: banned nowhere.
 * - **protected**: a ban would be refused — an allowlist, a proxy in front, the
 *   API itself, a private address (section 12's safety checks).
 */
export const AddressStatus = z.enum(['stopped', 'partly', 'pending', 'open', 'protected']);
/** One server's state for the address. `unknown`: its agent has not reported decisions. */
export const AddressServerState = z.enum(['banned', 'pending', 'failed', 'not_banned', 'unknown']);
export const AddressBan = z.object({
    by: DecisionBy,
    type: z.string().max(32),
    scenario: z.string().max(256).nullable(),
    /** The decision's own value: the address, or a range holding it. */
    value: z.string().max(64),
    until: IsoTime,
});
export const AddressServerVerdict = z.object({
    serverId: ServerId,
    serverName: z.string(),
    serverStatus: ServerStatus,
    state: AddressServerState,
    bans: z.array(AddressBan).max(20),
    /** Why a ban would not be sent here (no CrowdSec, a proxy in front…), when it would not. */
    skipped: z.string().max(300).nullable(),
    /** When its agent last reported what CrowdSec holds; null for one that never has. */
    decisionsReportedAt: IsoTime.nullable(),
    /** The address's events on this server over the last week. */
    events: z.number().int().nonnegative(),
    /** Its requests to this server's websites over the last day. */
    requests: z.number().int().nonnegative(),
    lastSeenAt: IsoTime.nullable(),
});
export const AddressEvent = z.object({
    id: z.uuid(),
    serverId: ServerId,
    category: z.string(),
    severity: z.string(),
    message: z.string(),
    occurredAt: IsoTime,
});
export const AddressSuggestion = z.object({
    id: z.uuid(),
    status: z.string(),
    recommendation: z.string(),
    title: z.string(),
    createdAt: IsoTime,
});
/** `GET /v1/addresses/:ip`. */
export const AddressVerdict = z.object({
    ip: IpAddress,
    status: AddressStatus,
    /** Why a ban would be refused for everyone, in the safety check's words; null when it would not. */
    protectedBy: z.string().max(500).nullable(),
    servers: z.array(AddressServerVerdict).max(5000),
    /** Over the fleet: events in the last week and requests in the last day. */
    events: z.number().int().nonnegative(),
    requests: z.number().int().nonnegative(),
    firstSeenAt: IsoTime.nullable(),
    lastSeenAt: IsoTime.nullable(),
    categories: z.record(z.string(), z.number().int().nonnegative()),
    recentEvents: z.array(AddressEvent).max(20),
    suggestion: AddressSuggestion.nullable(),
    actions: z.array(ActionSummary).max(10),
    checkedAt: IsoTime,
    /** Where it is and whose network (DB-IP, approximate); null before the database is fetched. */
    geo: GeoInfo.nullable().default(null),
});
export const MAX_ADDRESS_LOOKUP = 100;
/** `POST /v1/addresses/lookup`: the status of many at once, for a list's badges. */
export const AddressLookupRequest = z
    .object({ ips: z.array(IpAddress).min(1).max(MAX_ADDRESS_LOOKUP) })
    .strict();
export const AddressLookupRow = z.object({
    ip: IpAddress,
    status: AddressStatus,
    /** Servers it is banned on, and servers that could ban it. */
    bannedOn: z.number().int().nonnegative(),
    servers: z.number().int().nonnegative(),
});
export const AddressLookupResponse = z.object({
    addresses: z.array(AddressLookupRow).max(MAX_ADDRESS_LOOKUP),
});
/**
 * The verdict from per-server states, given the fleet's protection. Shared, so the
 * address page and a list's badge never disagree.
 */
export function addressStatus(protectedBy, states) {
    if (protectedBy !== null)
        return 'protected';
    const banned = states.filter((state) => state === 'banned').length;
    const reachable = states.filter((state) => state !== 'unknown').length;
    if (banned > 0)
        return banned >= reachable ? 'stopped' : 'partly';
    if (states.includes('pending'))
        return 'pending';
    return 'open';
}
//# sourceMappingURL=addresses.js.map